import {getLocalTimeZone, today} from '@internationalized/date'
import type {DateValue} from 'react-aria-components'

import flightBackgroundPng from '@/assets/flight/flight-background.png?lqip'
// import MultiCitySvg from '@/assets/flight/icons/booking-type/multi-city.svg?react'
import OneWaySvg from '@/assets/flight/icons/booking-type/one-way.svg?react'
import RoundTripSvg from '@/assets/flight/icons/booking-type/round-trip.svg?react'
import OutBoundDateSvg from '@/assets/flight/icons/out-bound-date.svg?react'
import ReturnDateSvg from '@/assets/flight/icons/return-date.svg?react'
import TicketClassSvg from '@/assets/flight/icons/ticket-class.svg?react'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import SkipLink from '@/components/SkipLink'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import {getAirports} from '@/lib/flight/flightService'
import {BookingType, TravelClass} from '@/lib/flight/types'
import {
  dateValueStringToDateValue,
  dateValueToDateValueString,
  getCalendarDateForToday,
  validatePassengers,
} from '@/lib/flight/utils'
import HeadTags from '@/lib/head/HeadTags'
import {FlightRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'

import InnosearchAirportSelector from './components/InnosearchAirportSelector'
import InnosearchDatePicker from './components/InnosearchDatePicker'
import InnosearchFlightPassengerSelector from './components/InnosearchFlightPassengerSelector'
import InnosearchSelect from './components/InnosearchSelect'
import style from './Flight.module.scss'
import SearchResult, {type FlightSearchResultControl} from './SearchResult'

const BOOKING_TYPES = [
  {label: 'Round trip', value: BookingType.RoundTrip, icon: <RoundTripSvg />},
  {label: 'One way', value: BookingType.OneWay, icon: <OneWaySvg />},
  // {label: 'Multi-city', value: BookingType.MultiCity, icon: <MultiCitySvg />},
]

const TICKET_CLASSES = [
  {label: 'Economy', value: TravelClass.Economy, icon: <TicketClassSvg />},
  {label: 'Premium economy', value: TravelClass.PremiumEconomy, icon: <TicketClassSvg />},
  {label: 'Business', value: TravelClass.Business, icon: <TicketClassSvg />},
  {label: 'First class', value: TravelClass.FirstClass, icon: <TicketClassSvg />},
]
export default memo(function Flight() {
  const navigate = useNavigate()
  const inputBoxRef = useRef<HTMLDivElement>(null)
  const imgBackgroundRef = useRef<HTMLImageElement>(null)

  const {
    type: bookingTypeSearch,
    class: ticketClassSearch,
    pax: {
      adult: numOfAdultSearch,
      children: numOfChildrenSearch,
      infantsInSeat: numOfInfantsInSeatSearch,
      infantsOnLap: numOfInfantsOnLapSearch,
    },
    departureDate: departureDateSearch,
    arrivalDate: arrivalDateSearch,
    departureAirport: departureAirportSearch,
    arrivalAirport: arrivalAirportSearch,
    token,
  } = FlightRoute.useSearch()

  const latestToken = useLatest(token)

  const [bookingType, setBookingType] = useState<BookingType>(bookingTypeSearch)
  const latestBookingType = useLatest(bookingType)

  const [ticketClass, setTicketClass] = useState<TravelClass>(ticketClassSearch)
  const latestTicketClass = useLatest(ticketClass)

  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- fix later
  const [passengerError, setPassengerErrorBase] = useState<string>('')
  const passengerErrorTimeout = useRef<NodeJS.Timeout | null>(null)
  const setPassengerError = useCallback((error: string) => {
    if (passengerErrorTimeout.current) clearTimeout(passengerErrorTimeout.current)
    setPassengerErrorBase(error)
    passengerErrorTimeout.current = setTimeout(() => {
      setPassengerErrorBase('')
    }, 10000)
  }, [])

  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- fix later
  const [numOfAdult, setNumOfAdultBase] = useState<number>(numOfAdultSearch ?? 1)
  const latestNumOfAdult = useLatest(numOfAdult)

  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- fix later
  const [numOfChildren, setNumOfChildrenBase] = useState<number>(numOfChildrenSearch ?? 0)
  const latestNumOfChildren = useLatest(numOfChildren)

  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- fix later
  const [numOfInfantsInSeat, setNumOfInfantsInSeatBase] = useState<number>(
    numOfInfantsInSeatSearch ?? 0,
  )
  const latestNumOfInfantsInSeat = useLatest(numOfInfantsInSeat)

  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- fix later
  const [numOfInfantsOnLap, setNumOfInfantsOnLapBase] = useState<number>(
    numOfInfantsOnLapSearch ?? 0,
  )
  const latestNumOfInfantsOnLap = useLatest(numOfInfantsOnLap)

  const setNumOfAdult = useCallback(
    (num: number) => {
      setPassengerError('')
      const result = validatePassengers(
        num,
        latestNumOfChildren.current,
        latestNumOfInfantsInSeat.current,
        latestNumOfInfantsOnLap.current,
      )
      if (result !== true) {
        setPassengerError(result)
        return
      }
      setNumOfAdultBase(num)
    },
    [setNumOfAdultBase, setPassengerError],
  )

  const setNumOfChildren = useCallback(
    (num: number) => {
      setPassengerError('')
      const result = validatePassengers(
        latestNumOfAdult.current,
        num,
        latestNumOfInfantsInSeat.current,
        latestNumOfInfantsOnLap.current,
      )
      if (result !== true) {
        setPassengerError(result)
        return
      }
      setNumOfChildrenBase(num)
    },
    [setNumOfChildrenBase, setPassengerError],
  )

  const setNumOfInfantsInSeat = useCallback(
    (num: number) => {
      setPassengerError('')
      const result = validatePassengers(
        latestNumOfAdult.current,
        latestNumOfChildren.current,
        num,
        latestNumOfInfantsOnLap.current,
      )
      if (result !== true) {
        setPassengerError(result)
        return
      }
      setNumOfInfantsInSeatBase(num)
    },
    [setNumOfInfantsInSeatBase, setPassengerError],
  )

  const setNumOfInfantsOnLap = useCallback(
    (num: number) => {
      setPassengerError('')
      const result = validatePassengers(
        latestNumOfAdult.current,
        latestNumOfChildren.current,
        latestNumOfInfantsInSeat.current,
        num,
      )
      if (result !== true) {
        setPassengerError(result)
        return
      }
      setNumOfInfantsOnLapBase(num)
    },
    [setNumOfInfantsOnLapBase, setPassengerError],
  )

  const [departureDate, setDepartureDate] = useState<DateValue | null>(
    departureDateSearch
      ? () => dateValueStringToDateValue(departureDateSearch)
      : getCalendarDateForToday,
  )
  const latestDepartureDate = useLatest(departureDate)
  const [departureDateError, setDepartureDateError] = useState<string>('')

  const [arrivalDate, setArrivalDate] = useState<DateValue | null>(
    arrivalDateSearch ? () => dateValueStringToDateValue(arrivalDateSearch) : null,
  )
  const latestArrivalDate = useLatest(arrivalDate)
  const [arrivalDateError, setArrivalDateError] = useState<string>('')

  const [departureAirportInputValue, setDepartureAirportInputValue] =
    useState<string>(departureAirportSearch)
  const [departureAirport, setDepartureAirport] = useState<string | null>(departureAirportSearch)
  const latestDepartureAirport = useLatest(
    departureAirport ?? departureAirportInputValue.toUpperCase(),
  )
  const [departureAirportError, setDepartureAirportError] = useState<string>('')

  const [arrivalAirportInputValue, setArrivalAirportInputValue] =
    useState<string>(arrivalAirportSearch)
  const [arrivalAirport, setArrivalAirport] = useState<string | null>(arrivalAirportSearch)
  const latestArrivalAirport = useLatest(arrivalAirport ?? arrivalAirportInputValue.toUpperCase())
  const [arrivalAirportError, setArrivalAirportError] = useState<string>('')

  const flightSearchResultRef = useRef<FlightSearchResultControl>(null)

  const resetErrors = useCallback(() => {
    setDepartureDateError('')
    setArrivalDateError('')
    setDepartureAirportError('')
    setArrivalAirportError('')
  }, [])

  const validateSearch = useCallback(
    async (isActive = true) => {
      if (isActive) resetErrors()

      console.log(latestDepartureAirport.current)
      console.log(latestArrivalAirport.current)

      let hasError = false

      if (!latestDepartureAirport.current) {
        if (isActive) setDepartureAirportError('Departure airport is required')
        hasError = true
      }

      if (!latestArrivalAirport.current) {
        if (isActive) setArrivalAirportError('Arrival airport is required')
        hasError = true
      }

      if (
        latestDepartureAirport.current &&
        latestDepartureAirport.current === latestArrivalAirport.current
      ) {
        if (isActive) setArrivalAirportError('Departure and arrival airports cannot be the same')
        hasError = true
      }

      if (!hasError) {
        const airports = await Promise.allSettled([
          getAirports({q: latestDepartureAirport.current}),
          getAirports({q: latestArrivalAirport.current}),
        ])

        // `airport` can be either a valid airport code, or a valid airport name
        // if its a valid airport name, we need to find the airport code
        ;(() => {
          if (airports[0].status === 'fulfilled' && airports[0].value.length > 0) {
            const airport = airports[0].value.find(
              airport =>
                airport.airport_name.toUpperCase() === latestDepartureAirport.current ||
                airport.airport_code.toUpperCase() === latestDepartureAirport.current,
            )
            if (airport) {
              setDepartureAirport(airport.airport_code)
              return
            }
          }

          if (isActive) setDepartureAirportError('Departure airport is not valid')
          hasError = true
        })()
        ;(() => {
          if (airports[1].status === 'fulfilled' && airports[1].value.length > 0) {
            const airport = airports[1].value.find(
              airport =>
                airport.airport_name.toUpperCase() === latestArrivalAirport.current ||
                airport.airport_code.toUpperCase() === latestArrivalAirport.current,
            )
            if (airport) {
              setArrivalAirport(airport.airport_code)
              return
            }
          }

          if (isActive) setArrivalAirportError('Arrival airport is not valid')
          hasError = true
        })()
      }

      /* eslint-disable require-atomic-updates -- no race condition since we only set hasError to true */
      if (!latestDepartureDate.current) {
        if (isActive) setDepartureDateError('Departure date is required')
        hasError = true
      } else if (latestDepartureDate.current < today(getLocalTimeZone())) {
        if (isActive) setDepartureDateError('Departure date cannot be in the past')
        hasError = true
      }

      if (latestBookingType.current === BookingType.RoundTrip && !latestArrivalDate.current) {
        if (isActive) setArrivalDateError('Return date is required')
        hasError = true
      }

      if (
        latestDepartureDate.current &&
        latestArrivalDate.current &&
        latestDepartureDate.current > latestArrivalDate.current
      ) {
        if (isActive) setArrivalDateError('Return date must be after departure date')
        hasError = true
      }
      /* eslint-enable require-atomic-updates */

      return hasError
    },
    [resetErrors],
  )

  const search = useCallback(
    async (restart = false, isActive = true) => {
      if (await validateSearch(isActive)) return

      flightSearchResultRef.current?.search({
        bookingType: latestBookingType.current,
        ticketClass: latestTicketClass.current,
        numOfAdult: latestNumOfAdult.current,
        numOfChildren: latestNumOfChildren.current,
        numOfInfantsInSeat: latestNumOfInfantsInSeat.current,
        numOfInfantsOnLap: latestNumOfInfantsOnLap.current,
        departureDate: latestDepartureDate.current,
        arrivalDate: latestArrivalDate.current,
        departureAirport: latestDepartureAirport.current,
        arrivalAirport: latestArrivalAirport.current,
        token: restart ? undefined : latestToken.current,
      })

      void navigate({
        to: FlightRoute.fullPath,
        search: {
          type: latestBookingType.current,
          class: latestTicketClass.current,
          pax: {
            adult: latestNumOfAdult.current,
            children: latestNumOfChildren.current,
            infantsInSeat: latestNumOfInfantsInSeat.current,
            infantsOnLap: latestNumOfInfantsOnLap.current,
          },
          departureDate: latestDepartureDate.current
            ? dateValueToDateValueString(latestDepartureDate.current)
            : undefined,
          arrivalDate: latestArrivalDate.current
            ? dateValueToDateValueString(latestArrivalDate.current)
            : undefined,
          departureAirport: latestDepartureAirport.current,
          arrivalAirport: latestArrivalAirport.current,
          token: restart ? undefined : latestToken.current,
        },
        resetScroll: false,
      })
    },
    [validateSearch, navigate],
  )

  useEffect(() => {
    void search(false, false)
  }, [token, search])

  useEffect(() => {
    function handleResize() {
      if (!inputBoxRef.current) return

      const header = document.getElementById('header')
      const headerHeight = header?.clientHeight ?? 0
      const top = inputBoxRef.current.getBoundingClientRect().top + window.scrollY
      const height = inputBoxRef.current.clientHeight

      const outcome = top + height / 2 - headerHeight

      if (imgBackgroundRef.current) {
        imgBackgroundRef.current.style.maxHeight = `${outcome}px`
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [inputBoxRef])

  return (
    <>
      <div className={style.Page}>
        <HeadTags title='Flight' />
        <SkipLink title='Skip to main content' to='#main-content' />
        <Navbar variant='dark' haveCart={false} />
        <main {...skipTargetProps('main-content')}>
          <div className={style.Container}>
            <img
              fetchPriority='high'
              src={flightBackgroundPng.src}
              alt=''
              className={style.Banner}
              style={{
                backgroundImage: `url("${flightBackgroundPng.lqip}")`,
                backgroundSize: 'cover',
                maxHeight: '725px',
              }}
              ref={imgBackgroundRef}
            />
            <h1 className={style.Title}>Innosearch Flight</h1>
            <div ref={inputBoxRef} className={clsx(style.InputBox, style.Dark)}>
              <div className={clsx(style.InputGroup, style.StillOneLine)} role='search'>
                <InnosearchSelect
                  label='Booking type'
                  options={BOOKING_TYPES}
                  selectedItem={bookingType}
                  setSelectedItem={setBookingType}
                />
                <InnosearchFlightPassengerSelector
                  numOfAdult={numOfAdult}
                  numOfChildren={numOfChildren}
                  numOfInfantsInSeat={numOfInfantsInSeat}
                  numOfInfantsOnLap={numOfInfantsOnLap}
                  onNumOfAdultChange={setNumOfAdult}
                  onNumOfChildrenChange={setNumOfChildren}
                  onNumOfInfantsInSeatChange={setNumOfInfantsInSeat}
                  onNumOfInfantsOnLapChange={setNumOfInfantsOnLap}
                  errorMessage={passengerError}
                />
                <InnosearchSelect
                  label='Ticket class'
                  options={TICKET_CLASSES}
                  selectedItem={ticketClass}
                  setSelectedItem={setTicketClass}
                />
              </div>
              <div
                className={clsx(style.InputGroup, style.FullWidth, {
                  [style.RoundTrip]: bookingType === BookingType.RoundTrip,
                })}
              >
                <InnosearchAirportSelector
                  icon={<OutBoundDateSvg />}
                  label='From airport'
                  placeholder='Where from?'
                  inputValue={departureAirportInputValue}
                  setInputValue={setDepartureAirportInputValue}
                  selectedAirport={departureAirport}
                  setSelectedAirport={setDepartureAirport}
                  errorMessage={departureAirportError}
                />
                <InnosearchAirportSelector
                  icon={<ReturnDateSvg />}
                  label='To airport'
                  placeholder='Where to?'
                  inputValue={arrivalAirportInputValue}
                  setInputValue={setArrivalAirportInputValue}
                  selectedAirport={arrivalAirport}
                  setSelectedAirport={setArrivalAirport}
                  errorMessage={arrivalAirportError}
                />
                <InnosearchDatePicker
                  label='Departure date'
                  value={departureDate}
                  onChange={setDepartureDate}
                  errorMessage={departureDateError}
                />
                {bookingType === BookingType.RoundTrip && (
                  <InnosearchDatePicker
                    label='Return date'
                    value={arrivalDate}
                    onChange={setArrivalDate}
                    minValue={departureDate}
                    errorMessage={arrivalDateError}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={clsx(style.Container, style.Search)}>
            <PrimaryButton size='xl' onPress={async () => search(true)}>
              Search flights
            </PrimaryButton>
          </div>
          <SearchResult ref={flightSearchResultRef} />
        </main>
      </div>
      <Footer />
    </>
  )
})
